// src/operator/console/audit-console.tsx
//
// Console de consultation des événements d'audit (table `audit_events`).
//
// Branche sur GET /api/audit (cf. src/backend/routes/audit.js). Le scoping
// tenant est géré côté serveur — la console se contente d'afficher.
//
// Pagination par cursor : `nextBefore` (ISO du plus ancien événement
// retourné) → bouton « Plus ancien » relance la requête avec `before=…`.

import { createSignal, createMemo, For, Show } from "solid-js";
import { createQuery } from "@tanstack/solid-query";

interface AuditEvent {
  id: string;
  ts: string;
  action: string;
  actor: string | null;
  peerId: string | null;
  sessionCode: string | null;
  tenantId: string | null;
  operatorUserId: string | null;
  detail: string | null;
  payload: unknown;
}

interface AuditResponse {
  ok: boolean;
  events: AuditEvent[];
  nextBefore: string | null;
}

async function jget<T>(url: string): Promise<T> {
  const r = await fetch(url, { credentials: "include" });
  if (!r.ok) {
    const data = (await r.json().catch(() => ({}))) as Record<string, unknown>;
    throw new Error((data?.error as string) || `${url} → ${r.status}`);
  }
  return r.json() as Promise<T>;
}

const LIMIT_CHOICES = [50, 100, 200] as const;

/** Format relatif court ("12s", "3m", "2h", "5j") + fallback ISO. */
function relativeTime(iso: string): string {
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return iso;
  const diff = Math.max(0, Date.now() - t);
  const s = Math.floor(diff / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}j`;
}

/** Tronque à `n` caractères avec ellipse. */
function truncate(s: string, n: number): string {
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + "…";
}

/** Encode CSV en échappant " et entourant si besoin. */
function csvCell(v: unknown): string {
  if (v === null || v === undefined) return "";
  const s = typeof v === "string" ? v : JSON.stringify(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function eventsToCsv(rows: AuditEvent[]): string {
  const header = ["ts", "action", "actor", "sessionCode", "peerId", "tenantId", "operatorUserId", "detail", "payload"];
  const lines = [header.join(",")];
  for (const r of rows) {
    lines.push(
      [
        csvCell(r.ts),
        csvCell(r.action),
        csvCell(r.actor),
        csvCell(r.sessionCode),
        csvCell(r.peerId),
        csvCell(r.tenantId),
        csvCell(r.operatorUserId),
        csvCell(r.detail),
        csvCell(r.payload),
      ].join(","),
    );
  }
  return lines.join("\n");
}

export function AuditConsolePage() {
  const [err, setErr] = createSignal<string | null>(null);

  // Filtres "appliqués" (utilisés par la queryKey) vs filtres "en cours
  // d'édition" (synchronisés sur les inputs). On applique au clic Rafraîchir
  // pour éviter de spammer le backend pendant la saisie.
  const [actionInput, setActionInput] = createSignal("");
  const [sessionInput, setSessionInput] = createSignal("");
  const [limitInput, setLimitInput] = createSignal<number>(100);

  const [appliedAction, setAppliedAction] = createSignal("");
  const [appliedSession, setAppliedSession] = createSignal("");
  const [appliedLimit, setAppliedLimit] = createSignal<number>(100);

  // Pile des "before" successifs — vide = première page (now).
  const [beforeStack, setBeforeStack] = createSignal<string[]>([]);

  const currentBefore = createMemo(() => {
    const s = beforeStack();
    return s.length > 0 ? s[s.length - 1] : null;
  });

  const queryUrl = createMemo(() => {
    const params = new URLSearchParams();
    if (appliedAction()) params.set("action", appliedAction());
    if (appliedSession()) params.set("sessionCode", appliedSession());
    params.set("limit", String(appliedLimit()));
    const b = currentBefore();
    if (b) params.set("before", b);
    return `/api/audit?${params.toString()}`;
  });

  const auditQ = createQuery<AuditResponse>(() => ({
    queryKey: ["audit", queryUrl()],
    queryFn: async () => {
      setErr(null);
      try {
        return await jget<AuditResponse>(queryUrl());
      } catch (e) {
        setErr(e instanceof Error ? e.message : String(e));
        throw e;
      }
    },
  }));

  function applyFilters() {
    setAppliedAction(actionInput().trim());
    setAppliedSession(sessionInput().trim());
    setAppliedLimit(limitInput());
    setBeforeStack([]); // reset pagination
  }

  function refresh() {
    setBeforeStack([]);
    auditQ.refetch();
  }

  function loadOlder() {
    const next = auditQ.data?.nextBefore;
    if (!next) return;
    setBeforeStack([...beforeStack(), next]);
  }

  function resetPagination() {
    setBeforeStack([]);
  }

  function downloadCsv() {
    const rows = auditQ.data?.events || [];
    if (rows.length === 0) {
      setErr("Aucun événement à exporter.");
      return;
    }
    const csv = eventsToCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `audit-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }

  const inputStyle = {
    background: "var(--bg-2, #1e293b)",
    color: "var(--text-1, #e8ecf3)",
    border: "1px solid var(--border, #334155)",
    "border-radius": "4px",
    padding: "4px 8px",
    "font-size": "12px",
  } as const;

  return (
    <div class="plugins-console" style={{ overflow: "auto" }}>
      <div class="plugins-console-header">
        <span class="plugins-console-title">
          Audit
          <span class="plugins-console-sub">événements opérateur — consultation &amp; export</span>
        </span>
      </div>

      <Show when={err()}>
        <div
          style={{
            background: "rgba(220,38,38,0.12)",
            border: "1px solid var(--danger, #dc2626)",
            color: "var(--danger, #f87171)",
            padding: "6px 10px",
            "border-radius": "4px",
            "font-size": "12px",
            "margin-bottom": "10px",
          }}
        >
          {err()}
        </div>
      </Show>

      {/* ── Barre de filtres ────────────────────────────────────────── */}
      <div class="rmm-table-wrap" style={{ "margin-bottom": "10px" }}>
        <div
          style={{
            display: "flex",
            gap: "6px",
            padding: "8px",
            "flex-wrap": "wrap",
            "align-items": "center",
          }}
        >
          <input
            style={{ ...inputStyle, "min-width": "180px" }}
            placeholder="action (ex: session.created)"
            value={actionInput()}
            onInput={(e) => setActionInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
          <input
            style={{ ...inputStyle, "min-width": "140px" }}
            placeholder="sessionCode"
            value={sessionInput()}
            onInput={(e) => setSessionInput(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") applyFilters();
            }}
          />
          <select
            style={inputStyle}
            value={String(limitInput())}
            onChange={(e) => setLimitInput(Number(e.currentTarget.value))}
          >
            <For each={LIMIT_CHOICES}>{(n) => <option value={String(n)}>{n}/page</option>}</For>
          </select>
          <button class="btn btn--primary btn--xs" type="button" onClick={applyFilters}>
            Filtrer
          </button>
          <button class="btn btn--xs" type="button" onClick={refresh}>
            ↺ Rafraîchir
          </button>
          <button class="btn btn--xs" type="button" onClick={downloadCsv}>
            ⬇ CSV
          </button>
          <div style={{ flex: "1 1 0" }} />
          <span style={{ "font-size": "11px", color: "var(--text-4, #94a3b8)" }}>
            {auditQ.data ? `${auditQ.data.events.length} événement(s)` : auditQ.isLoading ? "Chargement…" : ""}
          </span>
        </div>
      </div>

      {/* ── Table ──────────────────────────────────────────────────── */}
      <div class="rmm-table-wrap">
        <table class="rmm-table">
          <thead>
            <tr>
              <th style={{ width: "70px" }}>Quand</th>
              <th style={{ width: "180px" }}>Action</th>
              <th style={{ width: "160px" }}>Acteur</th>
              <th style={{ width: "110px" }}>Session</th>
              <th>Détail</th>
            </tr>
          </thead>
          <tbody>
            <Show
              when={(auditQ.data?.events || []).length > 0}
              fallback={
                <tr>
                  <td colspan="5" style={{ color: "var(--text-4)", "text-align": "center", padding: "14px" }}>
                    {auditQ.isLoading ? "Chargement…" : "Aucun événement."}
                  </td>
                </tr>
              }
            >
              <For each={auditQ.data?.events || []}>
                {(ev) => {
                  const detailStr = ev.detail || "";
                  const fullTitle = `${new Date(ev.ts).toISOString()}\n${detailStr}`;
                  return (
                    <tr>
                      <td
                        style={{
                          "font-size": "11px",
                          color: "var(--text-4)",
                          "white-space": "nowrap",
                        }}
                        title={new Date(ev.ts).toLocaleString()}
                      >
                        {relativeTime(ev.ts)}
                      </td>
                      <td>
                        <span class="tag" style={{ "font-family": "var(--font-mono)", "font-size": "11px" }}>
                          {ev.action}
                        </span>
                      </td>
                      <td
                        style={{
                          "font-size": "11px",
                          color: "var(--text-2)",
                          "white-space": "nowrap",
                          overflow: "hidden",
                          "text-overflow": "ellipsis",
                          "max-width": "160px",
                        }}
                        title={ev.actor || ""}
                      >
                        {ev.actor || "—"}
                      </td>
                      <td
                        style={{
                          "font-family": "var(--font-mono)",
                          "font-size": "11px",
                          color: "var(--text-3)",
                        }}
                      >
                        {ev.sessionCode || "—"}
                      </td>
                      <td
                        style={{
                          "font-size": "11px",
                          color: "var(--text-2)",
                        }}
                        title={fullTitle}
                      >
                        {truncate(detailStr, 80) || "—"}
                      </td>
                    </tr>
                  );
                }}
              </For>
            </Show>
          </tbody>
        </table>
      </div>

      {/* ── Pagination ─────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          gap: "6px",
          padding: "10px 0",
          "align-items": "center",
        }}
      >
        <Show when={beforeStack().length > 0}>
          <button class="btn btn--xs" type="button" onClick={resetPagination}>
            ⤴ Première page
          </button>
        </Show>
        <div style={{ flex: "1 1 0" }} />
        <Show when={auditQ.data?.nextBefore}>
          <button class="btn btn--xs" type="button" onClick={loadOlder}>
            Plus ancien →
          </button>
        </Show>
      </div>
    </div>
  );
}
